import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Upload, Trash2, LogOut, ImagePlus, GripVertical, Calendar, RefreshCw, Check, AlertCircle, MessageSquare, Plus, Star, DollarSign, Tag, Save, BarChart3, BookOpen, Instagram, Sparkles, ChevronRight, UserRound, FilePenLine } from "lucide-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  arrayMove,
  useSortable,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import IGMediaManager from "@/components/admin/IGMediaManager";
import AIStudio from "@/components/admin/AIStudio";
import GuestPortalManager from "@/components/admin/GuestPortalManager";
import PortalContentManager from "@/components/admin/PortalContentManager";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SECTIONS = [
  { id: "portada", label: "Portada (Las Villas)" },
  { id: "sala", label: "Sala" },
  { id: "cocina", label: "Cocina" },
  { id: "comedor", label: "Comedor" },
  { id: "recamara_1", label: "Recámara 1" },
  { id: "recamara_2", label: "Recámara 2" },
  { id: "recamara_3", label: "Recámara 3" },
  { id: "bano_1", label: "Baño completo 1" },
  { id: "bano_2", label: "Baño completo 2" },
  { id: "exterior", label: "Exterior" },
  { id: "playa", label: "Playa" },
  { id: "experiencias", label: "Experiencias" },
];
const PROPERTIES = [
  { id: "home", label: "Home (sitio general)" },
  { id: "loft", label: "Loft Pura Brisa" },
  { id: "casita", label: "La Casita Pura Vida" },
];
const HOME_SECTIONS = [{ id: "actividades", label: "Actividades del Home" }];
const TOKEN_KEY = "vpv_admin_token";
const photoUrl = (path) => `${BACKEND_URL}/api/files/${path}`;
const fmtMXN = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 }).format(n || 0);

function SortablePhoto({ photo, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: photo.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 1,
    opacity: isDragging ? 0.85 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`photo-${photo.id}`}
      className="group relative aspect-square bg-stone-200 border border-stone-200 overflow-hidden shadow-sm"
    >
      <img src={photoUrl(photo.storage_path)} alt={photo.original_filename} className="w-full h-full object-cover pointer-events-none select-none" />
      <button {...attributes} {...listeners} data-testid={`drag-handle-${photo.id}`} className="absolute top-2 left-2 z-10 bg-stone-900/90 hover:bg-stone-700 text-white p-3 md:p-2 cursor-grab active:cursor-grabbing transition-opacity opacity-100 md:opacity-0 md:group-hover:opacity-100" title="Arrastra para reordenar" aria-label="Mover">
        <GripVertical className="w-4 h-4" />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(photo.id); }}
        onPointerDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        data-testid={`delete-photo-${photo.id}`}
        className="absolute top-2 right-2 z-10 bg-stone-900/90 hover:bg-red-600 text-white p-3 md:p-2 transition-opacity opacity-100 md:opacity-0 md:group-hover:opacity-100"
        title="Eliminar"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

// ---------- PRICING MANAGER ----------
function PricingManager({ token }) {
  const [propertyId, setPropertyId] = useState("casita");
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/pricing/${propertyId}`, { headers: { Authorization: `Bearer ${token}` } });
      setConfig(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cargar precios");
    }
  }, [propertyId, token]);

  useEffect(() => { load(); }, [load]);

  const updateTier = (season, idx, key, value) => {
    setConfig((prev) => ({ ...prev, [season]: prev[season].map((t, i) => i === idx ? { ...t, [key]: key === "price" || key === "min_guests" || key === "max_guests" ? parseFloat(value) || 0 : value } : t) }));
  };
  const addTier = (season) => {
    setConfig((prev) => ({ ...prev, [season]: [...prev[season], { min_guests: 1, max_guests: 2, price: 0 }] }));
  };
  const removeTier = (season, idx) => {
    setConfig((prev) => ({ ...prev, [season]: prev[season].filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    setLoading(true);
    try {
      await api.put(`/admin/pricing/${propertyId}`, {
        baja: config.baja,
        alta: config.alta,
        deposit: config.deposit || 0,
        min_nights_high: config.min_nights_high || 2,
        min_nights_low: config.min_nights_low || 1,
        weekend_uplift_percent: parseFloat(config.weekend_uplift_percent) || 0,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Precios guardados");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setLoading(false);
    }
  };

  if (!config) return <p className="text-stone-500">Cargando precios…</p>;

  return (
    <div className="space-y-6" data-testid="pricing-manager">
      <div className="flex gap-2 flex-wrap">
        {PROPERTIES.filter((p) => p.id !== "home").map((p) => (
          <button
            key={p.id}
            data-testid={`pricing-prop-${p.id}`}
            onClick={() => setPropertyId(p.id)}
            className={`px-4 py-2 text-xs uppercase tracking-[0.15em] font-semibold border ${propertyId === p.id ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-300 hover:border-stone-500"}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="border border-stone-200 bg-white p-6">
        <p className="text-xs uppercase tracking-wider text-stone-500 mb-3">Estos son los precios directos que paga el cliente al reservar sin promoción. El sitio muestra un precio comparativo más alto (precio directo ÷ 0.9) como "precio Airbnb" tachado para resaltar el ahorro del 10%. Las promociones (código, quiz) aplican UNA sola vez sobre este precio directo.</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <label className="text-sm">
            <span className="block text-xs uppercase tracking-wider text-stone-500 mb-1">Depósito reembolsable (MXN)</span>
            <input type="number" value={config.deposit || 0} onChange={(e) => setConfig({ ...config, deposit: parseFloat(e.target.value) || 0 })} className="w-full border border-stone-300 px-3 py-2 rounded-none" />
          </label>
          <label className="text-sm">
            <span className="block text-xs uppercase tracking-wider text-stone-500 mb-1">Mín. noches temp. alta</span>
            <input type="number" value={config.min_nights_high || 2} onChange={(e) => setConfig({ ...config, min_nights_high: parseInt(e.target.value, 10) || 1 })} className="w-full border border-stone-300 px-3 py-2 rounded-none" />
          </label>
          <label className="text-sm">
            <span className="block text-xs uppercase tracking-wider text-stone-500 mb-1">Mín. noches temp. baja</span>
            <input type="number" value={config.min_nights_low || 1} onChange={(e) => setConfig({ ...config, min_nights_low: parseInt(e.target.value, 10) || 1 })} className="w-full border border-stone-300 px-3 py-2 rounded-none" />
          </label>
          <label className="text-sm">
            <span className="block text-xs uppercase tracking-wider text-stone-500 mb-1" title="Porcentaje extra aplicado a las noches de viernes y sábado. Ej: 20 = +20% más caro.">
              Recargo fin de semana (%)
            </span>
            <input
              type="number"
              min="0"
              max="100"
              step="5"
              data-testid="weekend-uplift-input"
              value={config.weekend_uplift_percent ?? 0}
              onChange={(e) => setConfig({ ...config, weekend_uplift_percent: parseFloat(e.target.value) || 0 })}
              className="w-full border border-stone-300 px-3 py-2 rounded-none"
              placeholder="0"
            />
            <span className="block text-[10px] text-stone-400 mt-1">Aplica a viernes y sábado. 0 = desactivado.</span>
          </label>
        </div>
      </div>

      {["baja", "alta"].map((season) => (
        <div key={season} className="border border-stone-200 bg-white p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-serif-display text-2xl">Tarifas — Temporada {season === "baja" ? "Baja" : "Alta"}</h3>
            <button onClick={() => addTier(season)} data-testid={`add-tier-${season}`} className="text-sm text-[#E07A5F] hover:underline">+ Agregar rango</button>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-stone-500 text-left">
                <th className="py-2">Mín huéspedes</th>
                <th className="py-2">Máx huéspedes</th>
                <th className="py-2">Precio por noche (MXN)</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {(config[season] || []).map((tier, idx) => (
                <tr key={idx} className="border-t border-stone-100" data-testid={`tier-${season}-${idx}`}>
                  <td className="py-2 pr-2"><input type="number" min="1" value={tier.min_guests} onChange={(e) => updateTier(season, idx, "min_guests", e.target.value)} className="w-24 border border-stone-300 px-2 py-1 rounded-none" /></td>
                  <td className="py-2 pr-2"><input type="number" min="1" value={tier.max_guests} onChange={(e) => updateTier(season, idx, "max_guests", e.target.value)} className="w-24 border border-stone-300 px-2 py-1 rounded-none" /></td>
                  <td className="py-2 pr-2"><input type="number" min="0" value={tier.price} onChange={(e) => updateTier(season, idx, "price", e.target.value)} className="w-32 border border-stone-300 px-2 py-1 rounded-none" /></td>
                  <td className="py-2"><button onClick={() => removeTier(season, idx)} className="text-stone-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      <Button onClick={save} disabled={loading} data-testid="save-pricing-btn" className="bg-[#E07A5F] hover:bg-[#D3674B] text-white rounded-none px-8 py-6 text-sm uppercase tracking-[0.18em] font-semibold">
        <Save className="w-4 h-4 mr-2" /> {loading ? "Guardando…" : "Guardar tarifas"}
      </Button>
    </div>
  );
}

// ---------- HIGH SEASON MANAGER ----------
function HighSeasonManager({ token }) {
  const [ranges, setRanges] = useState([]);
  const [form, setForm] = useState({ label: "", start: "", end: "", property_id: "*" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/high-season", { headers: { Authorization: `Bearer ${token}` } });
      setRanges(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.label || !form.start || !form.end) { toast.error("Completa todos los campos"); return; }
    try {
      await api.post("/admin/high-season", form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Rango agregado");
      setForm({ label: "", start: "", end: "", property_id: "*" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este rango?")) return;
    try {
      await api.delete(`/admin/high-season/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Eliminado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="space-y-6" data-testid="high-season-manager">
      <div className="border border-stone-200 bg-white p-6">
        <h3 className="font-serif-display text-2xl mb-3">Agregar rango de temporada alta</h3>
        <p className="text-xs text-stone-500 mb-4">Define las fechas en las que se aplican las tarifas de temporada alta. Aplica a ambas propiedades por defecto.</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input data-testid="hs-label" placeholder="Ej. Semana Santa 2026" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="border border-stone-300 px-3 py-2 text-sm rounded-none" />
          <input type="date" data-testid="hs-start" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} className="border border-stone-300 px-3 py-2 text-sm rounded-none" />
          <input type="date" data-testid="hs-end" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} className="border border-stone-300 px-3 py-2 text-sm rounded-none" />
          <select data-testid="hs-prop" value={form.property_id} onChange={(e) => setForm({ ...form, property_id: e.target.value })} className="border border-stone-300 px-3 py-2 text-sm rounded-none">
            <option value="*">Ambas propiedades</option>
            <option value="casita">Solo La Casita</option>
            <option value="loft">Solo Pura Brisa</option>
          </select>
        </div>
        <Button onClick={add} data-testid="hs-add-btn" className="mt-4 bg-stone-900 hover:bg-stone-700 text-white rounded-none px-6 py-3 text-xs uppercase tracking-[0.15em] font-semibold">
          <Plus className="w-4 h-4 mr-2" /> Agregar rango
        </Button>
      </div>

      <div className="border border-stone-200 bg-white">
        <h3 className="font-serif-display text-2xl px-6 pt-6 mb-3">Rangos definidos ({ranges.length})</h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-wider text-stone-500 text-left border-b border-stone-200">
              <th className="py-3 px-6">Etiqueta</th>
              <th className="py-3">Desde</th>
              <th className="py-3">Hasta</th>
              <th className="py-3">Aplica a</th>
              <th className="py-3 px-6"></th>
            </tr>
          </thead>
          <tbody>
            {ranges.map((r) => (
              <tr key={r.id} className="border-b border-stone-100" data-testid={`hs-row-${r.id}`}>
                <td className="py-3 px-6">{r.label}</td>
                <td className="py-3">{r.start}</td>
                <td className="py-3">{r.end}</td>
                <td className="py-3 text-stone-500">{r.property_id === "*" ? "Ambas" : r.property_id}</td>
                <td className="py-3 px-6"><button onClick={() => del(r.id)} className="text-stone-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button></td>
              </tr>
            ))}
            {ranges.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-stone-400">Sin rangos definidos</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- PROMOTIONS MANAGER ----------
function PromotionsManager({ token }) {
  const [promos, setPromos] = useState([]);
  const [form, setForm] = useState({
    label: "", code: "", property_id: "*", discount_type: "percent", discount_value: 5,
    valid_from: "", valid_to: "", auto_apply: false, active: true,
  });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/promotions", { headers: { Authorization: `Bearer ${token}` } });
      setPromos(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.label || !form.valid_from || !form.valid_to) { toast.error("Completa etiqueta y vigencia"); return; }
    try {
      await api.post("/admin/promotions", form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Promoción creada");
      setForm({ label: "", code: "", property_id: "*", discount_type: "percent", discount_value: 5, valid_from: "", valid_to: "", auto_apply: false, active: true });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const toggleActive = async (p) => {
    try {
      await api.put(`/admin/promotions/${p.id}`, { ...p, active: !p.active }, { headers: { Authorization: `Bearer ${token}` } });
      load();
    } catch (e) { toast.error("Error"); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar esta promoción?")) return;
    try {
      await api.delete(`/admin/promotions/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Eliminada");
      load();
    } catch (e) { toast.error("Error"); }
  };

  return (
    <div className="space-y-6" data-testid="promotions-manager">
      <div className="border border-stone-200 bg-white p-6">
        <h3 className="font-serif-display text-2xl mb-3">Nueva promoción</h3>
        <p className="text-xs text-stone-500 mb-4">Crea descuentos adicionales que se suman al 10% directo. Pueden ser automáticos por fechas o requerir un código.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input data-testid="promo-label" placeholder="Etiqueta (ej. Promo Verano)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="border border-stone-300 px-3 py-2 text-sm rounded-none" />
          <input data-testid="promo-code" placeholder="Código (opcional, ej. VERANO5)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="border border-stone-300 px-3 py-2 text-sm rounded-none uppercase" />
          <select data-testid="promo-prop" value={form.property_id} onChange={(e) => setForm({ ...form, property_id: e.target.value })} className="border border-stone-300 px-3 py-2 text-sm rounded-none">
            <option value="*">Ambas propiedades</option>
            <option value="casita">Solo La Casita</option>
            <option value="loft">Solo Pura Brisa</option>
          </select>
          <div className="flex gap-2">
            <select data-testid="promo-type" value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} className="flex-1 border border-stone-300 px-3 py-2 text-sm rounded-none">
              <option value="percent">% Porcentaje</option>
              <option value="amount">$ Monto fijo (MXN)</option>
            </select>
            <input type="number" data-testid="promo-value" min="0" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: parseFloat(e.target.value) || 0 })} className="w-28 border border-stone-300 px-3 py-2 text-sm rounded-none" />
          </div>
          <input type="date" data-testid="promo-from" value={form.valid_from} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} className="border border-stone-300 px-3 py-2 text-sm rounded-none" />
          <input type="date" data-testid="promo-to" value={form.valid_to} onChange={(e) => setForm({ ...form, valid_to: e.target.value })} className="border border-stone-300 px-3 py-2 text-sm rounded-none" />
        </div>
        <label className="flex items-center gap-2 mt-4 text-sm">
          <input type="checkbox" data-testid="promo-auto" checked={form.auto_apply} onChange={(e) => setForm({ ...form, auto_apply: e.target.checked })} />
          Aplicar automáticamente (sin código) a cualquier reserva dentro del rango
        </label>
        <Button onClick={add} data-testid="promo-add-btn" className="mt-4 bg-stone-900 hover:bg-stone-700 text-white rounded-none px-6 py-3 text-xs uppercase tracking-[0.15em] font-semibold">
          <Plus className="w-4 h-4 mr-2" /> Crear promoción
        </Button>
      </div>

      <div className="border border-stone-200 bg-white">
        <h3 className="font-serif-display text-2xl px-6 pt-6 mb-3">Promociones ({promos.length})</h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-wider text-stone-500 text-left border-b border-stone-200">
              <th className="py-3 px-6">Etiqueta</th>
              <th className="py-3">Código</th>
              <th className="py-3">Descuento</th>
              <th className="py-3">Vigencia</th>
              <th className="py-3">Auto</th>
              <th className="py-3">Activa</th>
              <th className="py-3 px-6"></th>
            </tr>
          </thead>
          <tbody>
            {promos.map((p) => (
              <tr key={p.id} className="border-b border-stone-100" data-testid={`promo-row-${p.id}`}>
                <td className="py-3 px-6">{p.label}</td>
                <td className="py-3 font-mono text-xs">{p.code || "—"}</td>
                <td className="py-3">{p.discount_type === "percent" ? `${p.discount_value}%` : fmtMXN(p.discount_value)}</td>
                <td className="py-3 text-stone-500">{p.valid_from} → {p.valid_to}</td>
                <td className="py-3">{p.auto_apply ? "Sí" : "No"}</td>
                <td className="py-3">
                  <button onClick={() => toggleActive(p)} className={`text-xs px-2 py-1 ${p.active ? "bg-[#81B29A]/20 text-[#3F6A53]" : "bg-stone-100 text-stone-500"}`}>{p.active ? "Activa" : "Inactiva"}</button>
                </td>
                <td className="py-3 px-6"><button onClick={() => del(p.id)} className="text-stone-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button></td>
              </tr>
            ))}
            {promos.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-stone-400">Sin promociones</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- PHOTOS + ICAL MANAGER ----------
function PhotosManager({ token }) {
  const [propertyId, setPropertyId] = useState("loft");
  const [sections, setSections] = useState({});
  const [activeSection, setActiveSection] = useState("sala");
  const [uploading, setUploading] = useState(false);
  const [icalConfig, setIcalConfig] = useState(null);
  const [icalInput, setIcalInput] = useState("");
  const [icalSaving, setIcalSaving] = useState(false);
  const [icalSyncing, setIcalSyncing] = useState(false);

  const visibleSections = propertyId === "home" ? HOME_SECTIONS : SECTIONS;
  useEffect(() => { setActiveSection(visibleSections[0]?.id || "portada"); /* eslint-disable-next-line */ }, [propertyId]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const loadPhotos = useCallback(async () => {
    try {
      const { data } = await api.get(`/photos?property_id=${propertyId}`);
      setSections(data.sections || {});
    } catch { toast.error("Error al cargar fotos"); }
  }, [propertyId]);

  const loadIcal = useCallback(async () => {
    if (propertyId === "home") { setIcalConfig(null); setIcalInput(""); return; }
    try {
      const { data } = await api.get(`/admin/properties/${propertyId}/ical`, { headers: { Authorization: `Bearer ${token}` } });
      setIcalConfig(data);
      setIcalInput(data?.ical_url || "");
    } catch { setIcalConfig(null); }
  }, [propertyId, token]);

  useEffect(() => { loadPhotos(); loadIcal(); }, [loadPhotos, loadIcal]);

  const handleUpload = async (e, sectionId) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("property_id", propertyId);
        fd.append("section", sectionId);
        fd.append("file", file);
        await api.post("/admin/photos", fd, { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${files.length} foto(s) subida(s)`);
      await loadPhotos();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al subir");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta foto?")) return;
    try {
      await api.delete(`/admin/photos/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Foto eliminada");
      await loadPhotos();
    } catch { toast.error("Error al eliminar"); }
  };

  const saveIcal = async () => {
    setIcalSaving(true);
    try {
      const { data } = await api.put(`/admin/properties/${propertyId}/ical`, { ical_url: icalInput.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      if (data.synced && data.count >= 0) toast.success(`iCal guardado y sincronizado · ${data.count} fechas bloqueadas`);
      else if (data.error) toast.error(`Guardado pero falló sincronización: ${data.error}`);
      else toast.success("iCal actualizado");
      await loadIcal();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al guardar"); }
    finally { setIcalSaving(false); }
  };

  const syncIcalNow = async () => {
    setIcalSyncing(true);
    try {
      const { data } = await api.post(`/admin/properties/${propertyId}/ical/sync`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (data.ok) toast.success(`Sincronizado · ${data.count} fechas bloqueadas`);
      else toast.error(data.error || "Error al sincronizar");
      await loadIcal();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al sincronizar"); }
    finally { setIcalSyncing(false); }
  };

  const handleDragEnd = async (event, sectionId) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const items = sections[sectionId] || [];
    const oldIndex = items.findIndex((p) => p.id === active.id);
    const newIndex = items.findIndex((p) => p.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const newItems = arrayMove(items, oldIndex, newIndex);
    setSections((prev) => ({ ...prev, [sectionId]: newItems }));
    try {
      await api.put("/admin/photos/reorder", { property_id: propertyId, section: sectionId, photo_ids: newItems.map((p) => p.id) }, { headers: { Authorization: `Bearer ${token}` } });
    } catch {
      toast.error("No se pudo guardar el nuevo orden");
      await loadPhotos();
    }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-8">
        {PROPERTIES.map((p) => (
          <button key={p.id} data-testid={`property-tab-${p.id}`} onClick={() => setPropertyId(p.id)} className={`px-5 py-3 text-sm uppercase tracking-[0.15em] font-semibold transition-colors border ${propertyId === p.id ? "bg-stone-900 text-stone-50 border-stone-900" : "bg-white text-stone-700 border-stone-300 hover:border-stone-500"}`}>
            {p.label}
          </button>
        ))}
      </div>

      <Tabs value={activeSection} onValueChange={setActiveSection}>
        {propertyId !== "home" && (
          <div data-testid="ical-card" className="border border-stone-200 bg-white p-6 md:p-8 mb-8">
            <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
              <div className="flex items-center gap-3">
                <Calendar className="w-6 h-6 text-[#E07A5F]" />
                <div>
                  <h3 className="font-serif-display text-2xl text-stone-900 leading-tight">Sincronización con Airbnb (iCal)</h3>
                  <p className="text-sm text-stone-500">Pega el link .ics que Airbnb te da en &quot;Exportar calendario&quot; para bloquear automáticamente las fechas reservadas.</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs">
                {icalConfig?.last_status === "ok" && <span className="inline-flex items-center gap-1.5 text-[#3F6A53] bg-[#81B29A]/15 border border-[#81B29A]/40 px-3 py-1.5"><Check className="w-3.5 h-3.5" /> Última sync OK · {icalConfig?.last_count ?? 0} fechas</span>}
                {icalConfig?.last_status === "error" && <span className="inline-flex items-center gap-1.5 text-red-700 bg-red-50 border border-red-200 px-3 py-1.5"><AlertCircle className="w-3.5 h-3.5" /> Error en última sync</span>}
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-3">
              <input type="url" value={icalInput} onChange={(e) => setIcalInput(e.target.value)} placeholder="https://www.airbnb.mx/calendar/ical/12345.ics?s=xxxxxxxx" data-testid="ical-url-input" className="flex-1 border border-stone-300 px-4 py-3 text-sm rounded-none focus:outline-none focus:border-stone-900 font-mono" />
              <Button onClick={saveIcal} disabled={icalSaving} data-testid="ical-save-btn" className="bg-stone-900 hover:bg-stone-700 text-white rounded-none px-6 py-3 text-xs uppercase tracking-[0.15em] font-semibold whitespace-nowrap">
                {icalSaving ? "Guardando…" : "Guardar y sincronizar"}
              </Button>
              <Button onClick={syncIcalNow} disabled={icalSyncing || !icalConfig?.ical_url} data-testid="ical-sync-btn" variant="outline" className="border-stone-900 text-stone-900 hover:bg-stone-900 hover:text-white rounded-none px-6 py-3 text-xs uppercase tracking-[0.15em] font-semibold whitespace-nowrap">
                <RefreshCw className={`w-4 h-4 mr-2 ${icalSyncing ? "animate-spin" : ""}`} />
                {icalSyncing ? "Sincronizando…" : "Sincronizar ahora"}
              </Button>
            </div>
            {icalConfig?.last_sync && <p className="mt-3 text-xs text-stone-400">Última sincronización: {new Date(icalConfig.last_sync).toLocaleString("es-MX")}{icalConfig?.last_error && <span className="text-red-600"> · {icalConfig.last_error}</span>}</p>}
          </div>
        )}

        <TabsList className="bg-stone-100 p-1 rounded-none h-auto flex flex-wrap gap-1 mb-6 w-full justify-start">
          {visibleSections.map((s) => (
            <TabsTrigger key={s.id} value={s.id} data-testid={`section-tab-${s.id}`} className="rounded-none data-[state=active]:bg-[#E07A5F] data-[state=active]:text-white text-stone-700 px-4 py-2 text-sm uppercase tracking-wider">
              {s.label}<span className="ml-2 text-xs opacity-70">({(sections[s.id] || []).length})</span>
            </TabsTrigger>
          ))}
        </TabsList>

        {visibleSections.map((s) => {
          const items = sections[s.id] || [];
          return (
            <TabsContent key={s.id} value={s.id} className="mt-0">
              <div className="border border-dashed border-stone-300 p-8 mb-6 bg-white text-center">
                <ImagePlus className="w-10 h-10 text-stone-400 mx-auto mb-3" />
                <p className="text-stone-700 mb-4">Arrastra o selecciona imágenes para <span className="font-semibold">{s.label}</span></p>
                <label className="inline-block">
                  <input type="file" multiple accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => handleUpload(e, s.id)} data-testid={`upload-input-${s.id}`} disabled={uploading} />
                  <span className={`cursor-pointer inline-flex items-center gap-2 bg-stone-900 hover:bg-stone-700 text-white px-6 py-3 text-sm uppercase tracking-[0.15em] font-semibold ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
                    <Upload className="w-4 h-4" />{uploading ? "Subiendo…" : "Seleccionar fotos"}
                  </span>
                </label>
                <p className="mt-3 text-xs text-stone-400">JPG, PNG o WebP · máx 8MB por foto</p>
              </div>

              {items.length === 0 ? (
                <p className="text-center text-stone-400 py-12 text-sm">Aún no hay fotos en esta sección.</p>
              ) : (
                <>
                  <p className="text-xs text-stone-500 mb-4">Arrastra el ícono <GripVertical className="inline w-3 h-3" /> de cada foto para reordenarlas. El orden se guarda automáticamente.</p>
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleDragEnd(e, s.id)}>
                    <SortableContext items={items.map((p) => p.id)} strategy={rectSortingStrategy}>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                        {items.map((photo) => <SortablePhoto key={photo.id} photo={photo} onDelete={handleDelete} />)}
                      </div>
                    </SortableContext>
                  </DndContext>
                </>
              )}
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
}

// ---------- RESERVATIONS MANAGER ----------
function ReservationsManager({ token }) {
  const [bookings, setBookings] = useState([]);
  const [caretakers, setCaretakers] = useState({ casita: "", loft: "" });
  const [statusFilter, setStatusFilter] = useState("all");
  const [propertyFilter, setPropertyFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (propertyFilter !== "all") params.set("property_id", propertyFilter);
      const { data } = await api.get(`/admin/bookings?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      setBookings(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cargar reservaciones");
    } finally { setLoading(false); }
  }, [statusFilter, propertyFilter, token]);

  const loadCaretakers = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/caretakers", { headers: { Authorization: `Bearer ${token}` } });
      setCaretakers(data || { casita: "", loft: "" });
    } catch {}
  }, [token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadCaretakers(); }, [loadCaretakers]);

  const fmtMxn = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 }).format(n || 0);

  const statusBadge = (s) => {
    const map = {
      paid: { bg: "#81B29A", color: "#1F3A2E", label: "Pagada" },
      pending: { bg: "#F2CC8F", color: "#5B4318", label: "Pendiente" },
      expired: { bg: "#E5E7EB", color: "#6B7280", label: "Expirada" },
      cancelled: { bg: "#FECACA", color: "#7F1D1D", label: "Cancelada" },
    };
    const m = map[s] || { bg: "#E5E7EB", color: "#374151", label: s };
    return <span className="text-[10px] px-2 py-1 uppercase tracking-wider font-semibold" style={{ background: m.bg, color: m.color }}>{m.label}</span>;
  };

  const sendToCaretaker = (b) => {
    const phone = (caretakers[b.property_id] || "").replace(/[^\d]/g, "");
    if (!phone) {
      toast.error(`Configura el WhatsApp del encargado en .env (CARETAKER_PHONE_${b.property_id.toUpperCase()})`);
      return;
    }
    const propName = b.property_name || (b.property_id === "casita" ? "La Casita Pura Vida" : "Loft Pura Brisa");
    const text = `🏖️ *NUEVA RESERVACIÓN — ${propName}*

📅 Check-in: *${b.check_in}*
📅 Check-out: *${b.check_out}*
🌙 Noches: ${b.nights || "—"}
👥 Huéspedes: ${b.guests}

👤 *Huésped:*
Nombre: ${b.name}
Email: ${b.email}
Teléfono: ${b.phone || "—"}

💵 Total: ${fmtMxn(b.total_web)}
${b.from_quiz ? "✨ Reservó con oferta del Quiz" : ""}

${b.message ? `📝 Mensaje:\n${b.message}` : ""}

Favor de preparar la casa, sábanas limpias, surtir agua y revisar todo antes del check-in. ¡Gracias!`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(text)}`, "_blank");
  };

  const deleteBooking = async (b) => {
    if (!window.confirm(`¿Borrar la reserva de ${b.name} (${b.check_in} → ${b.check_out})?\n\nEsta acción es irreversible.`)) return;
    try {
      await api.delete(`/admin/bookings/${b.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Reserva eliminada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const bulkDeleteTests = async () => {
    if (!window.confirm("¿Borrar TODAS las reservas pendientes, expiradas y canceladas? (las pagadas NO se tocan)")) return;
    try {
      const { data } = await api.post(
        "/admin/bookings/bulk-delete",
        { statuses: ["pending", "expired", "cancelled"] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${data.deleted_count} reserva(s) eliminada(s)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error en borrado masivo");
    }
  };

  return (
    <div className="space-y-6" data-testid="reservations-manager">
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <h3 className="font-serif-display text-2xl">Reservaciones ({bookings.length})</h3>
        <div className="flex gap-2 flex-wrap">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="filter-status" className="border border-stone-300 px-3 py-1.5 text-xs uppercase tracking-wider rounded-none">
            <option value="all">Todos estados</option>
            <option value="paid">Pagadas</option>
            <option value="pending">Pendientes</option>
            <option value="expired">Expiradas</option>
            <option value="cancelled">Canceladas</option>
          </select>
          <select value={propertyFilter} onChange={(e) => setPropertyFilter(e.target.value)} data-testid="filter-property" className="border border-stone-300 px-3 py-1.5 text-xs uppercase tracking-wider rounded-none">
            <option value="all">Ambas propiedades</option>
            <option value="casita">La Casita</option>
            <option value="loft">Pura Brisa</option>
          </select>
          <button onClick={load} className="px-3 py-1.5 text-xs uppercase tracking-wider border border-stone-300 hover:border-stone-900">
            <RefreshCw className={`w-3.5 h-3.5 inline ${loading ? "animate-spin" : ""}`} /> Refrescar
          </button>
          <button
            onClick={bulkDeleteTests}
            data-testid="bulk-delete-tests-btn"
            title="Borra todas las reservas pendientes/expiradas/canceladas (las pagadas NO se tocan)"
            className="px-3 py-1.5 text-xs uppercase tracking-wider border border-red-300 text-red-700 hover:bg-red-50"
          >
            🧹 Borrar pruebas
          </button>
        </div>
      </div>

      {(!caretakers.casita || !caretakers.loft) && (
        <div className="border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          ⚠️ <b>Sin WhatsApp configurado para el encargado.</b> Agrega <code className="bg-amber-100 px-1.5">CARETAKER_PHONE_CASITA</code> y <code className="bg-amber-100 px-1.5">CARETAKER_PHONE_LOFT</code> al .env del backend (formato 521234567890 sin +). Pídeselo al main agent en chat.
        </div>
      )}

      <div className="border border-stone-200 bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="text-xs uppercase tracking-wider text-stone-500 text-left border-b border-stone-200">
              <th className="py-3 px-4">Huésped</th>
              <th className="py-3">Propiedad</th>
              <th className="py-3">Check-in</th>
              <th className="py-3">Check-out</th>
              <th className="py-3">Noches · Personas</th>
              <th className="py-3">Total</th>
              <th className="py-3">Estado</th>
              <th className="py-3 px-4 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {bookings.map((b) => (
              <tr key={b.id} className="border-b border-stone-100" data-testid={`booking-${b.id}`}>
                <td className="py-3 px-4">
                  <div className="font-medium text-stone-900">{b.name}</div>
                  <div className="text-xs text-stone-500">{b.email}</div>
                  {b.phone && <div className="text-xs text-stone-500">{b.phone}</div>}
                </td>
                <td className="py-3 text-stone-700">{b.property_name || (b.property_id === "casita" ? "La Casita Pura Vida" : "Loft Pura Brisa")}</td>
                <td className="py-3 text-stone-700">{b.check_in}</td>
                <td className="py-3 text-stone-700">{b.check_out}</td>
                <td className="py-3 text-stone-700">{b.nights} · {b.guests}</td>
                <td className="py-3 text-stone-900 font-medium">{fmtMxn(b.total_web)}</td>
                <td className="py-3">{statusBadge(b.status)}</td>
                <td className="py-3 px-4 text-right">
                  <div className="inline-flex items-center gap-2">
                    <button
                      onClick={() => sendToCaretaker(b)}
                      data-testid={`send-caretaker-${b.id}`}
                      title="Enviar al encargado por WhatsApp"
                      className="inline-flex items-center gap-1.5 bg-[#25D366] hover:bg-[#1FB855] text-white px-3 py-2 text-xs uppercase tracking-wider font-semibold transition-colors"
                    >
                      <i className="fa-brands fa-whatsapp" /> Enviar
                    </button>
                    <button
                      onClick={() => deleteBooking(b)}
                      data-testid={`delete-booking-${b.id}`}
                      title="Eliminar reserva"
                      className="inline-flex items-center justify-center bg-white hover:bg-red-50 border border-red-300 text-red-700 px-2.5 py-2 text-xs transition-colors"
                    >
                      <i className="fa-solid fa-trash" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {bookings.length === 0 && (
              <tr><td colSpan={8} className="text-center py-12 text-stone-400">
                {loading ? "Cargando…" : "Sin reservaciones con estos filtros"}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- QUIZ STATS MANAGER ----------
const PROFILE_LABELS = {
  contemplativo: "🌅 Contemplativo",
  aventurero: "🏖️ Aventurero",
  romantico: "🍷 Romántico",
  festivo: "🎉 Festivo",
};

function QuizStatsManager({ token }) {
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/quiz/stats?days=${days}`, { headers: { Authorization: `Bearer ${token}` } });
      setStats(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cargar stats");
    } finally { setLoading(false); }
  }, [days, token]);

  useEffect(() => { load(); }, [load]);

  if (!stats) return <p className="text-stone-500" data-testid="stats-loading">{loading ? "Cargando…" : "Sin datos"}</p>;

  const f = stats.funnel;
  const r = stats.rates;
  const maxFunnel = Math.max(f.starts || 1, f.completed || 1);
  const funnelSteps = [
    { label: "Iniciaron el quiz", value: f.starts },
    { label: "Respondieron Q1", value: f.q1 },
    { label: "Respondieron Q2", value: f.q2 },
    { label: "Respondieron Q3", value: f.q3 },
    { label: "Respondieron Q4", value: f.q4 },
    { label: "Respondieron Q5", value: f.q5 },
    { label: "Vieron paso de email", value: f.email_step },
    { label: "Completaron (email entregado)", value: f.completed },
  ];

  const totalProfiles = Object.values(stats.profiles || {}).reduce((a, b) => a + b, 0) || 1;

  const fmtMxn = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 }).format(n || 0);

  return (
    <div className="space-y-8" data-testid="quiz-stats-manager">
      {/* Period selector */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-serif-display text-2xl">Métricas del Quiz</h3>
        <div className="flex gap-2">
          {[7, 30, 90, 365].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`stats-range-${d}`}
              className={`px-3 py-1.5 text-xs uppercase tracking-[0.15em] font-semibold border ${days === d ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-300 hover:border-stone-500"}`}
            >
              {d} días
            </button>
          ))}
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="border border-stone-200 bg-white p-5">
          <p className="text-xs uppercase tracking-wider text-stone-500 mb-2">Completados</p>
          <p className="font-serif-display text-3xl text-stone-900" data-testid="kpi-completed">{f.completed}</p>
          <p className="text-xs text-stone-500 mt-1">de {f.starts} starts</p>
        </div>
        <div className="border border-stone-200 bg-white p-5">
          <p className="text-xs uppercase tracking-wider text-stone-500 mb-2">Tasa completado</p>
          <p className="font-serif-display text-3xl text-[#E07A5F]" data-testid="kpi-completion-rate">{r.completion_rate_pct}%</p>
        </div>
        <div className="border border-stone-200 bg-white p-5">
          <p className="text-xs uppercase tracking-wider text-stone-500 mb-2">Quiz → Reserva pagada</p>
          <p className="font-serif-display text-3xl text-[#3F6A53]" data-testid="kpi-quiz-paid">{r.quiz_to_paid_rate_pct}%</p>
          <p className="text-xs text-stone-500 mt-1">{stats.bookings.paid_from_quiz} reservas</p>
        </div>
        <div className="border border-stone-200 bg-white p-5">
          <p className="text-xs uppercase tracking-wider text-stone-500 mb-2">Attach del Kit</p>
          <p className="font-serif-display text-3xl text-stone-900" data-testid="kpi-kit-attach">{r.kit_attach_rate_pct}%</p>
          <p className="text-xs text-stone-500 mt-1">{stats.bookings.with_kit}/{stats.bookings.total} reservas</p>
        </div>
      </div>

      {/* Revenue */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="border border-stone-200 bg-white p-5">
          <p className="text-xs uppercase tracking-wider text-stone-500 mb-2">Ingresos totales (pagado)</p>
          <p className="font-serif-display text-2xl">{fmtMxn(stats.revenue_mxn.total_paid)}</p>
        </div>
        <div className="border border-stone-200 bg-[#FCEFE6] p-5">
          <p className="text-xs uppercase tracking-wider text-[#E07A5F] mb-2">Ingresos del Quiz</p>
          <p className="font-serif-display text-2xl text-[#E07A5F]" data-testid="revenue-from-quiz">{fmtMxn(stats.revenue_mxn.from_quiz_paid)}</p>
        </div>
        <div className="border border-stone-200 bg-white p-5">
          <p className="text-xs uppercase tracking-wider text-stone-500 mb-2">Ingresos del Kit</p>
          <p className="font-serif-display text-2xl">{fmtMxn(stats.revenue_mxn.kit_addons_paid)}</p>
        </div>
      </div>

      {/* Funnel bar chart */}
      <div className="border border-stone-200 bg-white p-6">
        <h4 className="font-serif-display text-xl mb-5">Funnel del Quiz</h4>
        <div className="space-y-3">
          {funnelSteps.map((s, i) => {
            const pct = maxFunnel ? Math.round((s.value / maxFunnel) * 100) : 0;
            return (
              <div key={s.label} className="space-y-1" data-testid={`funnel-step-${i}`}>
                <div className="flex justify-between text-xs">
                  <span className="text-stone-600">{s.label}</span>
                  <span className="font-mono font-semibold text-stone-900 tabular-nums">{s.value}</span>
                </div>
                <div className="h-3 bg-stone-100 overflow-hidden">
                  <div className="h-3 bg-gradient-to-r from-[#E07A5F] to-[#D3674B] transition-all" style={{ width: `${Math.max(pct, 2)}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Profiles donut (bars) */}
      <div className="border border-stone-200 bg-white p-6">
        <h4 className="font-serif-display text-xl mb-5">Distribución de perfiles</h4>
        {Object.keys(stats.profiles || {}).length === 0 ? (
          <p className="text-sm text-stone-400">Aún sin respuestas.</p>
        ) : (
          <div className="space-y-3">
            {Object.entries(stats.profiles).sort((a, b) => b[1] - a[1]).map(([key, count]) => {
              const pct = Math.round((count / totalProfiles) * 100);
              return (
                <div key={key} data-testid={`profile-${key}`}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-stone-700">{PROFILE_LABELS[key] || key}</span>
                    <span className="font-mono text-stone-900 tabular-nums">{count} · {pct}%</span>
                  </div>
                  <div className="h-3 bg-stone-100 overflow-hidden">
                    <div className="h-3 bg-stone-900" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <p className="text-xs text-stone-400">
        Período: últimos {stats.days} días · {stats.bookings.total} reservas totales · {stats.bookings.from_quiz} provienen del quiz
      </p>
    </div>
  );
}

// ---------- TESTIMONIALS MANAGER ----------
function TestimonialsManager({ token }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", location: "", date: "", rating: 5, text: "", property_id: "" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/testimonials");
      setItems(data || []);
    } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.name.trim() || !form.text.trim()) { toast.error("Nombre y texto son obligatorios"); return; }
    try {
      await api.post("/admin/testimonials", form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Reseña agregada");
      setForm({ name: "", location: "", date: "", rating: 5, text: "", property_id: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar esta reseña?")) return;
    try {
      await api.delete(`/admin/testimonials/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Eliminada");
      load();
    } catch (e) { toast.error("Error"); }
  };

  return (
    <div className="space-y-6" data-testid="testimonials-manager">
      <div className="border border-stone-200 bg-white p-6">
        <h3 className="font-serif-display text-2xl mb-3">Agregar reseña</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <input data-testid="t-name" placeholder="Nombre del huésped" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border border-stone-300 px-4 py-2 text-sm rounded-none" />
          <input data-testid="t-location" placeholder="Ubicación (ej. CDMX)" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="border border-stone-300 px-4 py-2 text-sm rounded-none" />
          <input data-testid="t-date" placeholder="Fecha (ej. Jun 2025)" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="border border-stone-300 px-4 py-2 text-sm rounded-none" />
          <select data-testid="t-rating" value={form.rating} onChange={(e) => setForm({ ...form, rating: parseInt(e.target.value, 10) })} className="border border-stone-300 px-4 py-2 text-sm rounded-none">
            {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} estrellas</option>)}
          </select>
        </div>
        <textarea data-testid="t-text" placeholder="Texto de la reseña…" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} className="w-full border border-stone-300 px-4 py-2 text-sm rounded-none min-h-[100px] mb-3" />
        <Button onClick={add} data-testid="add-testimonial-btn" className="bg-stone-900 hover:bg-stone-700 text-white rounded-none px-6 py-3 text-xs uppercase tracking-[0.15em] font-semibold">
          Agregar reseña
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((t) => (
          <div key={t.id} data-testid={`existing-t-${t.id}`} className="border border-stone-200 p-4 bg-stone-50 relative">
            <button onClick={() => del(t.id)} className="absolute top-3 right-3 text-stone-500 hover:text-red-600">
              <Trash2 className="w-4 h-4" />
            </button>
            <div className="flex gap-1 mb-2">
              {Array.from({ length: t.rating || 5 }).map((_, i) => <Star key={i} className="w-3 h-3 text-[#F2CC8F] fill-[#F2CC8F]" />)}
            </div>
            <p className="text-sm text-stone-700 mb-2 line-clamp-3">&quot;{t.text}&quot;</p>
            <p className="text-xs text-stone-500">{t.name} {t.location && `· ${t.location}`} {t.date && `· ${t.date}`}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- MAIN ADMIN ----------
export default function Admin() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [topTab, setTopTab] = useState("fotos");

  const token = () => localStorage.getItem(TOKEN_KEY);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes("session_id=")) {
      const sessionId = new URLSearchParams(hash.slice(1)).get("session_id");
      window.history.replaceState({}, "", "/admin");
      (async () => {
        try {
          const { data } = await api.post("/auth/session", { session_id: sessionId });
          localStorage.setItem(TOKEN_KEY, data.session_token);
          setUser(data);
          toast.success(`Bienvenido, ${data.name || data.email}`);
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Error al iniciar sesión");
        } finally { setAuthChecked(true); }
      })();
      return;
    }
    const t = token();
    if (!t) { setAuthChecked(true); return; }
    (async () => {
      try {
        const { data } = await api.get("/auth/me", { headers: { Authorization: `Bearer ${t}` } });
        setUser(data);
      } catch { localStorage.removeItem(TOKEN_KEY); }
      finally { setAuthChecked(true); }
    })();
  }, []);

  const login = () => {
    const redirect = encodeURIComponent(`${window.location.origin}/admin`);
    window.location.href = `https://auth.emergentagent.com/?redirect=${redirect}`;
  };

  const logout = async () => {
    try { await api.post("/auth/logout", {}, { headers: { Authorization: `Bearer ${token()}` } }); } catch {}
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    navigate("/");
  };

  if (!authChecked) {
    return <div data-testid="admin-loading" className="min-h-screen flex items-center justify-center bg-[#FAFAF9]"><p className="text-stone-500">Cargando…</p></div>;
  }

  if (!user) {
    return (
      <div data-testid="admin-login" className="min-h-screen flex items-center justify-center bg-[#FAFAF9] px-6">
        <div className="max-w-md w-full text-center py-24">
          <p className="text-eyebrow text-[#E07A5F] mb-3">Villas Pura Vida</p>
          <h1 className="font-serif-display text-5xl mb-4">Panel admin</h1>
          <p className="text-stone-600 mb-10">Inicia sesión con tu cuenta de Google para gestionar el sitio.</p>
          <Button data-testid="login-google-btn" onClick={login} className="bg-[#E07A5F] hover:bg-[#D3674B] text-white rounded-none px-8 py-6 text-sm uppercase tracking-[0.18em] font-semibold">
            <i className="fa-brands fa-google mr-3" /> Iniciar sesión con Google
          </Button>
          <p className="mt-6 text-xs text-stone-400">
            <Link to="/" className="hover:text-stone-700">← Volver al sitio</Link>
          </p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: "fotos", label: "Fotos & iCal", icon: ImagePlus },
    { id: "reservas", label: "Reservaciones", icon: BookOpen },
    { id: "precios", label: "Precios", icon: DollarSign },
    { id: "temporada", label: "Temporada alta", icon: Calendar },
    { id: "promos", label: "Promociones", icon: Tag },
    { id: "stats", label: "Stats Quiz", icon: BarChart3 },
    { id: "resenas", label: "Reseñas", icon: MessageSquare },
    { id: "ig", label: "Galería IG", icon: Instagram },
    { id: "ai", label: "Estudio IA", icon: Sparkles },
    { id: "portal", label: "Portal huésped", icon: UserRound },
    { id: "portal-cms", label: "Experiencia · Contenido", icon: FilePenLine },
  ];

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#FAFAF9] text-stone-900">
      <header className="border-b border-stone-200 bg-stone-50/90 backdrop-blur sticky top-0 z-50">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="font-serif-display text-2xl">Villas <span className="text-[#E07A5F]">Pura Vida</span></Link>
            <span className="text-eyebrow text-stone-400 border-l border-stone-300 pl-4">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            {user.picture && <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />}
            <span className="text-sm text-stone-600 hidden md:inline">{user.email}</span>
            <Button variant="ghost" onClick={logout} data-testid="logout-btn" className="rounded-none text-stone-700 hover:text-[#E07A5F]">
              <LogOut className="w-4 h-4 mr-2" /> Salir
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-10 flex flex-col lg:flex-row gap-8 lg:gap-10">
        {/* Sidebar izquierdo */}
        <aside className="lg:w-64 lg:flex-shrink-0 lg:sticky lg:top-24 lg:self-start">
          <p className="text-eyebrow text-[#E07A5F] mb-3">Panel de administración</p>
          <h1 className="font-serif-display text-3xl mb-6 leading-tight">Gestiona<br/>tu sitio</h1>
          <nav className="flex flex-row lg:flex-col gap-1 overflow-x-auto lg:overflow-visible border-b lg:border-b-0 lg:border-l border-stone-200 pb-2 lg:pb-0 lg:pl-0 -mx-6 px-6 lg:mx-0 lg:px-0">
            {tabs.map((t) => {
              const Icon = t.icon;
              const active = topTab === t.id;
              return (
                <button
                  key={t.id}
                  data-testid={`top-tab-${t.id}`}
                  onClick={() => setTopTab(t.id)}
                  className={`group flex-shrink-0 lg:flex-shrink lg:w-full inline-flex items-center gap-3 px-4 py-3 text-[13px] uppercase tracking-[0.14em] font-semibold transition-all lg:justify-between whitespace-nowrap border-l-2 ${active ? "bg-stone-900 text-white border-[#E07A5F] lg:pl-5" : "text-stone-600 hover:text-stone-900 hover:bg-stone-100 border-transparent"}`}
                >
                  <span className="inline-flex items-center gap-3">
                    <Icon className={`w-4 h-4 ${active ? "text-[#E07A5F]" : ""}`} />
                    <span>{t.label}</span>
                  </span>
                  <ChevronRight className={`w-3.5 h-3.5 hidden lg:inline transition-transform ${active ? "translate-x-0.5 text-[#E07A5F]" : "opacity-0 group-hover:opacity-50"}`} />
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Contenido */}
        <main className="flex-1 min-w-0">
          {topTab === "fotos" && <PhotosManager token={token()} />}
          {topTab === "reservas" && <ReservationsManager token={token()} />}
          {topTab === "precios" && <PricingManager token={token()} />}
          {topTab === "temporada" && <HighSeasonManager token={token()} />}
          {topTab === "promos" && <PromotionsManager token={token()} />}
          {topTab === "stats" && <QuizStatsManager token={token()} />}
          {topTab === "resenas" && <TestimonialsManager token={token()} />}
          {topTab === "ig" && <IGMediaManager token={token()} />}
          {topTab === "ai" && <AIStudio token={token()} />}
          {topTab === "portal" && <GuestPortalManager token={token()} />}
          {topTab === "portal-cms" && <PortalContentManager token={token()} />}
        </main>
      </div>
    </div>
  );
}
